import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Mail, Phone, Instagram, MapPin, ArrowUpRight, CheckCircle2, Star } from "lucide-react";
import { SITE, SERVICES } from "@/lib/site";
import { toast } from "sonner";
import useSEO from "@/hooks/useSEO";
import { trackQuoteSubmitted, trackPhoneClick, trackReviewClick } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  useSEO({
    title: "Contact · Request a Free Quote",
    description: "Request a free quote and visual mockup for your custom signs, architectural wraps, window film, wall graphics, or vinyl installation project. Toronto & GTA.",
    path: "/contact",
  });
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    service: SERVICES[0].title, project_size: "", location: "", timeline: "", message: ""
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Prefill from Local Lead Finder → "Convert to quote" deep-link.
  // /contact?from_lead=1&company=…&phone=…&email=…&location=…&message=…
  const [params] = useSearchParams();
  useEffect(() => {
    if (params.get("from_lead") !== "1") return;
    setForm((f) => ({
      ...f,
      company:  params.get("company")  || f.company,
      phone:    params.get("phone")    || f.phone,
      email:    params.get("email")    || f.email,
      location: params.get("location") || f.location,
      message:  params.get("message")  || f.message,
    }));
    // Scroll form into view so it's obvious what to fill.
    setTimeout(() => document.querySelector('[data-testid="quote-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (k) => (e) => setForm(s => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      trackQuoteSubmitted(form.service);
      setDone(true);
      toast.success("Quote request sent. We'll get back within 24 hours.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="pt-36 pb-12 bg-ink-950 border-b border-white/5" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="eyebrow gold-bar">Contact</div>
          <h1 className="font-display text-5xl md:text-7xl mt-4 tracking-tight">Request a free quote.</h1>
          <p className="text-white/60 max-w-2xl mt-5 text-lg">Share a few details and we'll respond within 24 hours with pricing and a visual mockup.</p>
        </div>
      </section>

      <section className="py-20 bg-ink-950">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Form */}
          <div className="lg:col-span-7">
            {done ? (
              <div className="card-dark p-12 text-center" data-testid="quote-success">
                <CheckCircle2 size={56} className="text-gold mx-auto mb-6"/>
                <h2 className="font-display text-3xl">Thanks — request received.</h2>
                <p className="text-white/60 mt-4 max-w-md mx-auto">We'll be in touch within one business day. For urgent work, call us directly.</p>
                <a href={SITE.phoneHref} className="btn-gold mt-8 inline-flex">{SITE.phone}</a>
              </div>
            ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="quote-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Full name *">
                  <input data-testid="form-name" required value={form.name} onChange={update("name")} className="form-input"/>
                </Field>
                <Field label="Email *">
                  <input data-testid="form-email" required type="email" value={form.email} onChange={update("email")} className="form-input"/>
                </Field>
                <Field label="Phone">
                  <input data-testid="form-phone" value={form.phone} onChange={update("phone")} className="form-input"/>
                </Field>
                <Field label="Company">
                  <input data-testid="form-company" value={form.company} onChange={update("company")} className="form-input"/>
                </Field>
                <Field label="Service *">
                  <select data-testid="form-service" value={form.service} onChange={update("service")} className="form-input">
                    {SERVICES.map(s => <option key={s.slug} value={s.title}>{s.title}</option>)}
                    <option value="Multiple / Not sure">Multiple / Not sure</option>
                  </select>
                </Field>
                <Field label="Project size">
                  <select data-testid="form-project-size" value={form.project_size} onChange={update("project_size")} className="form-input">
                    <option value="">Select…</option>
                    <option>Small (under 100 sq ft)</option>
                    <option>Medium (100–500 sq ft)</option>
                    <option>Large (500+ sq ft)</option>
                    <option>Multi-location</option>
                  </select>
                </Field>
                <Field label="Location">
                  <input data-testid="form-location" placeholder="Toronto, Mississauga, …" value={form.location} onChange={update("location")} className="form-input"/>
                </Field>
                <Field label="Timeline">
                  <select data-testid="form-timeline" value={form.timeline} onChange={update("timeline")} className="form-input">
                    <option value="">Select…</option>
                    <option>ASAP (within 2 weeks)</option>
                    <option>1 month</option>
                    <option>2–3 months</option>
                    <option>Flexible</option>
                  </select>
                </Field>
              </div>
              <Field label="Project details *">
                <textarea data-testid="form-message" required rows={6} value={form.message} onChange={update("message")} placeholder="Tell us about your space, sizes, surfaces, branding, and goals…" className="form-input resize-none"/>
              </Field>
              <button type="submit" disabled={loading} data-testid="form-submit" className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed">
                {loading ? "Sending…" : <>Send Request <ArrowUpRight size={14}/></>}
              </button>
            </form>
            )}
          </div>

          {/* Side panel */}
          <aside className="lg:col-span-5">
            <div className="card-dark p-8">
              <div className="eyebrow">Direct line</div>
              <h3 className="font-display text-2xl mt-3">Skip the form.</h3>
              <p className="text-white/55 text-sm mt-3">Need it fast? Call or text — we usually answer within minutes during business hours.</p>
              <div className="mt-6 space-y-4">
                <a href={SITE.phoneHref} data-testid="contact-phone" className="flex items-center gap-4 p-4 bg-ink-800 hover:border-gold border border-transparent transition-colors">
                  <Phone className="text-gold" size={20}/>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-white/45">Call us</div>
                    <div className="text-white font-medium">{SITE.phone}</div>
                  </div>
                </a>
                <a href={SITE.emailHref} data-testid="contact-email" className="flex items-center gap-4 p-4 bg-ink-800 hover:border-gold border border-transparent transition-colors">
                  <Mail className="text-gold" size={20}/>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-white/45">Email</div>
                    <div className="text-white font-medium break-all">{SITE.email}</div>
                  </div>
                </a>
                <a href={SITE.instagramUrl} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="flex items-center gap-4 p-4 bg-ink-800 hover:border-gold border border-transparent transition-colors">
                  <Instagram className="text-gold" size={20}/>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-white/45">Instagram</div>
                    <div className="text-white font-medium">{SITE.instagram}</div>
                  </div>
                </a>
              </div>

              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="eyebrow">Past customer?</div>
                <a
                  href={SITE.gbpReviewUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={trackReviewClick}
                  data-testid="contact-review-cta"
                  className="mt-3 flex items-center justify-between gap-4 p-5 bg-ink-800 hover:bg-ink-900 border border-white/10 hover:border-gold transition-all group"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-gold text-gold"/>)}
                    </div>
                    <div>
                      <div className="text-white font-medium text-sm">Review us on Google</div>
                      <div className="text-white/45 text-[11px] mt-0.5">Helps a small Toronto business · 30 seconds</div>
                    </div>
                  </div>
                  <ArrowUpRight className="text-gold group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" size={18}/>
                </a>
              </div>
              <div className="mt-8 pt-8 border-t border-white/10">
                <div className="eyebrow">Service area</div>
                <div className="mt-3 flex items-start gap-3 text-white/70 text-sm">
                  <MapPin className="text-purple-hi shrink-0 mt-0.5" size={16}/>
                  <p>{SITE.areas.join(" · ")}</p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <style>{`
        .form-input {
          width: 100%;
          background: #1A1A1A;
          border: 1px solid rgba(255,255,255,0.1);
          color: white;
          padding: 14px 16px;
          font-size: 14px;
          border-radius: 2px;
          transition: all .25s ease;
          outline: none;
          font-family: 'Outfit', sans-serif;
        }
        .form-input:focus {
          border-color: #D4AF37;
          box-shadow: 0 0 0 1px rgba(212,175,55,0.4);
        }
        .form-input::placeholder { color: rgba(255,255,255,0.3); }
      `}</style>
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2">{label}</span>
      {children}
    </label>
  );
}
